import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogContent, DialogActions, 
  Button, TextField, Box, Autocomplete, Typography, Avatar, Paper
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import Heading from '../../../WebApp/Components/Heading/heading.tsx';

interface Contact {
  name: string;
  phone: string;
}

interface AddParentModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (phone: string) => void;
  contacts?: Contact[];
}

const AddParentModal: React.FC<AddParentModalProps> = ({ open, onClose, onAdd, contacts = [] }) => {
  const [value, setValue] = useState<Contact | string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');

  useEffect(() => {
    if (open) {
      setValue(null);
      setInputValue('');
      setPhoneError('');
    }
  }, [open]);

  const handleAddClick = () => {
    let finalPhone = '';

    if (typeof value === 'object' && value !== null) {
      finalPhone = value.phone; 
    } else if (typeof value === 'string' && value.trim() !== '') {
      finalPhone = value; 
    } else {
      finalPhone = inputValue; 
    }

    finalPhone = finalPhone.replace(/\s+/g, ''); 

    let hasError = false;

    if (!finalPhone) {
      setPhoneError('Phone number or contact is required');
      hasError = true;
    } else if (/\D/.test(finalPhone)) {
      setPhoneError('Please enter a valid 10-digit number or select a contact');
      hasError = true;
    } else if (!/^[6-9]/.test(finalPhone)) {
      setPhoneError('Phone number must start with 6, 7, 8, or 9');
      hasError = true;
    } else if (finalPhone.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits');
      hasError = true;
    }

    if (hasError) return;

    onAdd(finalPhone); 
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: '16px', padding: 0 }
      }}
    >
      <Heading title="Add Parent" closeIcon="true" onClose={onClose} />
      
      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            freeSolo
            options={[...contacts].sort((a, b) => a.name.localeCompare(b.name))}
            
            PaperComponent={(props) => (
              <Paper 
                elevation={6} 
                sx={{ 
                  borderRadius: '12px', 
                  mt: 1, 
                  overflow: 'hidden',
                  border: '1px solid #eaeaea'
                }} 
                {...props} 
              />
            )}
            
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.name} (${option.phone})`;
            }}
            
            filterOptions={(options, state) => {
              const input = state.inputValue.toLowerCase();
              return options.filter(
                (opt) => 
                  opt.name.toLowerCase().includes(input) || 
                  opt.phone.includes(input)
              );
            }}
            
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
              setPhoneError('');
            }}
            
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              setInputValue(newInputValue);
              
              if (reason === 'input' || reason === 'clear') {
                setValue(null);
              }
              
              if (/^[\d\s]+$/.test(newInputValue)) {
                const digits = newInputValue.replace(/\s/g, '');
                if (digits.length > 0 && !/^[6-9]/.test(digits)) {
                  setPhoneError('Phone number must start with 6, 7, 8, or 9');
                } else {
                  setPhoneError('');
                }
              } else {
                setPhoneError(''); 
              }
            }}
            
            renderOption={(props, option) => (
              <Box 
                component="li" 
                {...props} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  padding: '10px 16px',
                  borderBottom: '1px solid #f5f5f5',
                  transition: 'background-color 0.2s ease',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { backgroundColor: '#f9f9f9' }
                }}
              >
                {/* Contact Initial Avatar */}
                <Avatar 
                  sx={{ 
                    bgcolor: 'rgba(211, 47, 47, 0.08)', 
                    color: '#d32f2f', 
                    width: 38, 
                    height: 38,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  {option.name ? option.name.charAt(0).toUpperCase() : '?'}
                </Avatar>

                {/* Contact Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.95rem' }}
                  >
                    {option.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                    <PhoneIcon sx={{ fontSize: '12px', color: '#888' }} />
                    <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.8rem' }}>
                      {option.phone}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            
            renderInput={(params) => (
              <TextField
                {...params}
                label="Name or Number"
                variant="outlined"
                fullWidth
                size="medium"
                error={Boolean(phoneError)}
                helperText={phoneError}
                type="text"
                InputProps={{
                  ...params.InputProps,
                  sx: { borderRadius: '10px' }
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
            <DialogActions style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>

          <Button onClick={onClose} className="custom-cancel-button">
          Cancel
        </Button>
  <Button 
          onClick={handleAddClick} 
          className="custom-button"
          variant="contained" 
          sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddParentModal;